
import { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import './_headerNavigation.scss'

import { DropdownNavigation } from 'components-ref/organisms/dropdownNavigation/DropdownNavigation'

import {
  AuthModalProvider,
  useAuthModalContext
} from 'pages/landingPage/components/AuthmodalContext'

import { LoginForm } from 'pages/auth/view/LoginForm'
import {
  Register,
  registrationOpen
} from 'pages/auth/view/Register/Register'
import { RecoverAccount } from 'pages/auth/view/RecoverAccount/RecoverAccount'
import { ResetPassword } from 'pages/auth/view/RecoverAccount/ResetPassword'

import { useAccessToken, useUserProfile } from 'store'
import { usePendingNotificationsStore } from 'store/pendingNotificationsStore'

import avatarPlaceholder from '../../assets/images/user_placeholder.png'
import { AllIcons } from '../../helpers/allIcons'

type HeaderNavigationProps = {
  hasInfoCircle?: boolean
  infoCircleData?: number
}

type GuestAuthPanel =
  | 'default'
  | 'login'
  | 'signup'
  | 'forgetPassword'
  | 'resetPassword'


/*
 * Guest navigation uses the SAME authentication modal
 * provider as the landing page.
 *
 * This preserves:
 * - Landing page modal styling
 * - Circular AllFans logo
 * - Terms of Service / Privacy Policy
 * - Login / Sign Up footer links
 * - Login / registration modal dimensions
 */
const GuestHeaderNavigation = () => {
  const modalData = useAuthModalContext()

  useEffect(() => {
    const handleGuestLogin = () => {
      openGuestAuthModal('login')
    }

    window.addEventListener('open-guest-login', handleGuestLogin)

    return () => {
      window.removeEventListener('open-guest-login', handleGuestLogin)
    }
  }, [])


  const openGuestAuthModal = (panel: GuestAuthPanel) => {
    if (panel === 'default') {
      modalData.clearModal()
      return
    }

    if (panel === 'login') {
      modalData.addModal(
        'login',
        <LoginForm setActivePanel={openGuestAuthModal} />,
        true,
        false
      )
      return
    }

    if (panel === 'signup') {
      modalData.addModal(
        'signup',
        <Register />,
        true,
        false,
        true
      )
      return
    }

    if (panel === 'forgetPassword') {
      modalData.addModal(
        'forgetPassword',
        <RecoverAccount
          setActivePanel={openGuestAuthModal}
        />,
        true,
        false
      )
      return
    }

    if (panel === 'resetPassword') {
      modalData.addModal(
        'resetPassword',
        <ResetPassword
          setActivePanel={openGuestAuthModal as any}
        />,
        true,
        false
      )
    }
  }

  return (
    <div className='header-right header-right--guest'>
      <div className='pf-header__actions'>
        <button
          type='button'
          onClick={() => openGuestAuthModal('login')}
          className='btn btn-default'
          data-login
        >
          Login
        </button>

        <button
          type='button'
          onClick={() => openGuestAuthModal('signup')}
          className='btn btn-primary'
          data-signup
        >
          {registrationOpen ? 'Sign up' : 'Pre Register'}
        </button>
      </div>
    </div>
  )
}

/*
 * Existing authenticated navigation.
 * Guest navigation uses its own provider above.
 */
function HeaderNavigation({
  hasInfoCircle,
  infoCircleData
}: HeaderNavigationProps) {
  const { t } = useTranslation()

  const userData = useUserProfile()
  const isAuthenticated = useAccessToken() !== ''

  const navigate = useNavigate()
  const location = useLocation()

  const notificationsCount = usePendingNotificationsStore(
    s => s.notifications
  )

  const resetNotifications = usePendingNotificationsStore(
    s => s.resetNotifications
  )

  const onNotificationsPage = useMemo(
    () =>
      location.pathname === '/notifications' ||
      location.pathname === '/notifications/fans' ||
      location.pathname === '/notifications/site',
    [location.pathname]
  )

  useEffect(() => {
    if (
      onNotificationsPage &&
      notificationsCount > 0
    ) {
      resetNotifications()
    }
  }, [
    onNotificationsPage,
    notificationsCount,
    resetNotifications
  ])

  /*
   * Unauthenticated:
   * Mount the same modal provider used by LandingPage.
   */
  if (!isAuthenticated) {
    return (
      <AuthModalProvider>
        <GuestHeaderNavigation />
      </AuthModalProvider>
    )
  }

  /*
   * Authenticated:
   * Original header navigation, unchanged.
   */
  return (
    <>
      <div className='header-right'>
        {/* Notifications */}
        <Link
          className='footer__link'
          to={{ pathname: '/notifications' }}
          onClick={e => {
            if (!isAuthenticated) {
              e.preventDefault()

              navigate(
                `/${
                  userData?.role === 'model'
                    ? userData.username
                    : 'menu'
                }`
              )
            }
          }}
          aria-label={t('notifications')}
        >
          {notificationsCount > 0 && (
            <div
              className='footer__link__status'
              aria-live='polite'
              aria-label={`${notificationsCount} ${t(
                'notifications'
              )}`}
            >
              {notificationsCount}
            </div>
          )}

          <img
            src={AllIcons.footer_notifications}
            alt={t('notifications')}
            className='header-icon'
            width='24'
            height='24'
            decoding='async'
          />
        </Link>

        {/* Profile */}
        <div
          className='profile__menu'
          onClick={() => {
            if (userData?.role !== 'fan') {
              navigate(
                `/${
                  userData?.role === 'model'
                    ? userData.username
                    : 'menu'
                }`
              )
            }
          }}
          role={
            userData?.role !== 'fan'
              ? 'button'
              : undefined
          }
          aria-label={t('profile')}
          style={{
            cursor:
              userData?.role !== 'fan'
                ? 'pointer'
                : 'default'
          }}
        >
          <img
            className='header-avatar'
            src={
              userData?.avatar?.url ??
              avatarPlaceholder
            }
            alt={t('profile')}
            onError={e => {
              ;(
                e.currentTarget as HTMLImageElement
              ).onerror = null

              ;(
                e.currentTarget as HTMLImageElement
              ).src = avatarPlaceholder
            }}
            decoding='async'
          />
        </div>

        <DropdownNavigation />
      </div>
    </>
  )
}

export default HeaderNavigation
